import {
    ref as $ref,
    atom as $atom,
    pathValue as $value
} from '@graphistry/falcor-json-graph';

import { Observable } from '@reactivex/rxjs';
import { SELECT_TOOLBAR_ITEM } from '@graphistry/viz/src/actions/toolbar';

export function toolbar(action$, store) {
    return selectToolbarItem(action$, store).ignoreElements();
}

const reducers = {
    'zoom-in': zoomIn,
    'zoom-out': zoomOut,
    'center-camera': centerCamera,
    'open-workbook': openWorkbook,
    'fork-workbook': forkWorkbook,
    'save-workbook': saveWorkbook,
    'embed-workbook': embedWorkbook,
    'share-workbook': shareWorkbook,
    'fullscreen-workbook': fullscreenWorkbook,
    'toggle-filters': toggleFilters,
    'toggle-legend': toggleLegend,
    'toggle-timebar': toggleTimebar,
    'toggle-inspector': toggleInspector,
    'toggle-histograms': toggleHistograms,
    'toggle-exclusions': toggleExclusions,
    'toggle-simulating': toggleSimulating,
    'toggle-select-nodes': toggleSelectNodes,
    'toggle-window-nodes': toggleWindowNodes,
    'toggle-label-settings': toggleLabelSettings,
    'toggle-scene-settings': toggleSceneSettings,
    'toggle-layout-settings': toggleLayoutSettings,
    'toggle-collections': toggleCollections,
    'toggle-kepler-dropdown': toggleKeplerDropdown,
    'take-screenshot': takeScreenshot,
    'toggle-kepler-hybrid': toggleKeplerHybrid,
    'toggle-kepler-map': toggleKeplerMap,
    'toggle-kepler-graph': toggleKeplerGraph
};

function selectToolbarItem(action$, store) {
    return action$
        .ofType(SELECT_TOOLBAR_ITEM)
        .throttleTime(10)
        .groupBy(({ id }) => id)
        .mergeMap(actionsById => actionsById.switchMap(({ id, ...props }) => reducers[id]({ id, ...props })));
}

function zoomIn({ falcor }) {
    return Observable.concat(
        centerCamera({ falcor, selected: true }),
        falcor.getValue(`camera.zoom`).mergeMap((zoom) =>
            falcor.set($value(`camera.zoom`, zoom * (1 / 1.25))))
    );
}

function zoomOut({ falcor }) {
    return Observable.concat(
        centerCamera({ falcor, selected: true }),
        falcor.getValue(`camera.zoom`).mergeMap((zoom) =>
            falcor.set($value(`camera.zoom`, zoom * 1.25)))
    );
}

function centerCamera({ falcor, selected }) {
    const values = [
        $value(`camera.center.auto`, !selected),
        $value(`camera.controls[2].selected`, !selected)
    ];
    !selected && values.push(
        $value(`camera.zoom`, $atom(1, { $timestamp: Date.now() })),
        $value(`camera.center['x', 'y', 'z']`, $atom(0, { $timestamp: Date.now() }))
    );

    return falcor.set(...values);
}

function toggleSimulating({ falcor, selected }) {
    return falcor.set(
        $value(`scene.simulating`, !selected),
        $value(`scene.controls[0].selected`, !selected)
    );
}

function toggleFilters({ falcor, selected }) {
    if (selected) {
        return falcor.set(
            $value(`panels.left`, undefined),
            $value(`filters.controls[0].selected`, false)
        );
    } else {
        return falcor.set(
            $value(`filters.controls[0].selected`, true),
            $value(`scene.controls[1].selected`, false),
            $value(`labels.controls[0].selected`, false),
            $value(`layout.controls[0].selected`, false),
            $value(`exclusions.controls[0].selected`, false),
            $value(`panels.left`, $ref(falcor._path.concat(`filters`)))
        );
    }
}

function toggleLegend({ falcor, selected }) {
    return falcor.set(
        $value(`legend.visible`, !selected),
        $value(`legend.controls[0].selected`, !selected)
    );
}

function toggleExclusions({ falcor, selected }) {
    if (selected) {
        return falcor.set(
            $value(`panels.left`, undefined),
            $value(`exclusions.controls[0].selected`, false)
        );
    } else {
        return falcor.set(
            $value(`exclusions.controls[0].selected`, true),
            $value(`scene.controls[1].selected`, false),
            $value(`labels.controls[0].selected`, false),
            $value(`layout.controls[0].selected`, false),
            $value(`filters.controls[0].selected`, false),
            $value(`panels.left`, $ref(falcor._path.concat(`exclusions`)))
        );
    }
}

function toggleHistograms({ falcor, selected }) {
    return falcor.set(
        $value(`histograms.visible`, !selected),
        $value(`histograms.controls[0].selected`, !selected),
        ...(selected ? [$value(`highlight.darken`, false)] : [])
    );
}

function toggleTimebar({ falcor, selected }) {
    if (selected) {
        return falcor.set(
            $value(`panels.bottom`, undefined),
            $value(`timebars.controls[0].selected`, false)
        );
    } else {
        return falcor.set(
            $value(`timebars.controls[0].selected`, true),
            $value(`inspector.controls[0].selected`, false),
            $value(`panels.bottom`, $ref(falcor._path.concat(`timebars`)))
        );
    }
}

function toggleInspector({ falcor, selected }) {
    if (selected) {
        return falcor.set(
            $value(`panels.bottom`, undefined),
            $value(`inspector.controls[0].selected`, false)
        );
    } else {
        return falcor.set(
            $value(`inspector.controls[0].selected`, true),
            $value(`timebars.controls[0].selected`, false),
            $value(`panels.bottom`, $ref(falcor._path.concat(`inspector`)))
        );
    }
}

function toggleLabelSettings({ falcor, selected }) {
    if (selected) {
        return falcor.set(
            $value(`panels.left`, undefined),
            $value(`labels.controls[0].selected`, false)
        );
    } else {
        return falcor.set(
            $value(`labels.controls[0].selected`, true),
            $value(`scene.controls[1].selected`, false),
            $value(`layout.controls[0].selected`, false),
            $value(`filters.controls[0].selected`, false),
            $value(`exclusions.controls[0].selected`, false),
            $value(`panels.left`, $ref(falcor._path.concat(`labels`)))
        );
    }
}

function toggleSceneSettings({ falcor, selected }) {
    if (selected) {
        return falcor.set(
            $value(`panels.left`, undefined),
            $value(`scene.controls[1].selected`, false)
        );
    } else {
        return falcor.set(
            $value(`scene.controls[1].selected`, true),
            $value(`labels.controls[0].selected`, false),
            $value(`layout.controls[0].selected`, false),
            $value(`filters.controls[0].selected`, false),
            $value(`exclusions.controls[0].selected`, false),
            $value(`panels.left`, $ref(falcor._path.concat(`scene`)))
        );
    }
}

function toggleLayoutSettings({ falcor, selected }) {
    if (selected) {
        return falcor.set(
            $value(`panels.left`, undefined),
            $value(`layout.controls[0].selected`, false)
        );
    } else {
        return falcor.set(
            $value(`layout.controls[0].selected`, true),
            $value(`scene.controls[1].selected`, false),
            $value(`labels.controls[0].selected`, false),
            $value(`filters.controls[0].selected`, false),
            $value(`exclusions.controls[0].selected`, false),
            $value(`panels.left`, $ref(falcor._path.concat(`layout`)))
        );
    }
}

function toggleSelectNodes({ falcor, selected }) {
    if (selected) {
        return falcor.set(
            $value(`selection.cursor`, 'auto'),
            $value(`selection['mask', 'type']`, null),
            $value(`selection.controls[0].selected`, false)
        );
    } else {
        // falcor.invalidate(`inspector.rows`, `selection.histogramsById`);
        return falcor.set(
            $value(`selection.mask`, null),
            $value(`selection.type`, 'select'),
            $value(`selection.cursor`, 'down'),
            $value(`selection.controls[0].selected`, true),
            $value(`selection.controls[1].selected`, false)
        );
    }
}

function toggleWindowNodes({ falcor, selected }) {
    if (selected) {
        falcor.invalidate(`inspector.rows`, `selection.histogramsById`);
        return falcor.set(
            $value(`selection.cursor`, 'auto'),
            $value(`selection['mask', 'type']`, null),
            $value(`selection.controls[1].selected`, false)
        );
    } else {
        return falcor.set(
            $value(`selection.type`, 'window'),
            $value(`selection.cursor`, 'down'),
            $value(`selection.controls[1].selected`, true),
            $value(`selection.controls[0].selected`, false),
            $value(`histograms.controls[0].selected`, true),
            $value(`panels.right`, $ref(falcor._path.concat(`histograms`)))
        );
    }
}

function openWorkbook({ falcor, selected }) {
    return Observable.defer(() => {
        const url = window.location.origin + window.location.pathname + window.location.search;
        window.open(url);
    });
}

function forkWorkbook({ falcor, selected }) {
    return Observable.empty();
}

function saveWorkbook({ falcor }) {
    const wbFalcor = falcor._clone({ _path: falcor._path.slice(0, -2) });
    return wbFalcor.call(['save']);
}

//Handled directly in toolbar b/c an overlay
function shareWorkbook({ falcor, selected }) {
    return Observable.empty();
}

function embedWorkbook({ falcor, selected }) {
    return Observable.empty();
}

function fullscreenWorkbook({ falcor, selected }) {
    return Observable.defer(() => {
        const isFullscreen = function() {
            return !(
                (document.fullScreenElement && document.fullScreenElement !== null) ||
                (!document.mozFullScreen && !document.webkitIsFullScreen)
            );
        };

        // http://stackoverflow.com/questions/3900701/onclick-go-full-screen
        if (isFullscreen()) {
            if (document.cancelFullScreen) {
                document.cancelFullScreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.webkitCancelFullScreen) {
                document.webkitCancelFullScreen();
            }
        } else {
            const documentElement = document.documentElement;
            if (documentElement.requestFullScreen) {
                documentElement.requestFullScreen();
            } else if (documentElement.mozRequestFullScreen) {
                documentElement.mozRequestFullScreen();
            } else if (documentElement.webkitRequestFullScreen) {
                documentElement.webkitRequestFullScreen(Element.ALLOW_KEYBOARD_INPUT);
            }
        }
    });
}

function toggleCollections({ falcor, selected }) {
    return falcor.getValue('collections.visible')
        .mergeMap(isVisible => {
            if (isVisible) {
                return falcor.set(
                    $value(`collections.visible`, false),
                    $value(`collections.controls[0].selected`, false)
                );
            } else {
                return falcor.set(
                    $value(`collections.visible`, true),
                    $value(`collections.controls[0].selected`, true)
                );
            }
        });
}

function toggleKeplerDropdown({ falcor, selected }) {
    return falcor.getValue('kepler.dropdownVisible')
        .mergeMap(isVisible => {
            const next = !isVisible;
            return falcor.set(
                $value(`kepler.dropdownVisible`, next)
            );
        });
}

function takeScreenshot() {
    return Observable.defer(async () => {
        try {
            // Request screen capture
            const mediaStream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    displaySurface: 'browser'  // Prefer current tab
                },
                audio: false,
                preferCurrentTab: true  // Chrome 94+ hint to prefer current tab
            });

            // Get video track
            const video = document.createElement('video');
            video.srcObject = mediaStream;
            video.autoplay = true;

            // Wait for video to be ready
            await new Promise(resolve => {
                video.onloadedmetadata = resolve;
            });

            // Play the video (required for capture)
            await video.play();

            // No delay - screenshot frame before info banner initiates a resize
            // Create canvas and capture frame
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');

            // Draw the current video frame
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            // Stop the media stream
            mediaStream.getTracks().forEach(track => track.stop());

            // Convert to blob and download
            canvas.toBlob(blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `graphistry-${Date.now()}.png`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 'image/png', 1.0);  // Max quality PNG
        } catch (error) {
            if (error.name === 'NotAllowedError') {
                console.error('Screen capture was denied by the user');
            } else if (error.name === 'NotSupportedError') {
                console.error('Screen capture is not supported in this browser');
            } else {
                console.error('Error capturing screen:', error);
            }
        }
    });
}

function toggleKeplerGraph({ falcor, selected }) {
    return falcor.set(
        $value(`kepler.visible`, false),
        $value(`kepler.mode`, 'graph'),
        $value(`kepler.dropdownVisible`, false),
        $value(`kepler.controls[0].selected`, true),
        $value(`kepler.controls[1].selected`, false),
        $value(`kepler.controls[2].selected`, false)
    );
}

function toggleKeplerHybrid({ falcor, selected }) {
    return falcor.set(
        $value(`kepler.visible`, true),
        $value(`kepler.mode`, 'hybrid'),
        $value(`kepler.dropdownVisible`, false),
        $value(`kepler.controls[0].selected`, false),
        $value(`kepler.controls[1].selected`, true),
        $value(`kepler.controls[2].selected`, false)
    );
}

function toggleKeplerMap({ falcor, selected }) {
    return falcor.set(
        $value(`kepler.visible`, true),
        $value(`kepler.mode`, 'kepler'),
        $value(`kepler.dropdownVisible`, false),
        $value(`kepler.controls[0].selected`, false),
        $value(`kepler.controls[1].selected`, false),
        $value(`kepler.controls[2].selected`, true)
    );
}
